/**
 * Phase 21β-4 — Geschäftspapier-Versionen (Admin-only).
 *
 * - Liste aller gespeicherten Versionen
 * - „Aktive Version snapshoten" → neue Version erstellen
 * - PDF-Vorschau pro Version (öffnet in neuem Tab)
 * - „Aktiv setzen" → ersetzt Mandanten-Settings durch diesen Snapshot
 *   (Sicherheits-Backup wird automatisch erzeugt)
 * - Löschen
 *
 * Wer das Panel sieht, kann lesen. Schreib-Aktionen (Snapshot/Aktiv/Löschen)
 * sind backend-seitig auf ADMIN_ROLES beschränkt; das Frontend versteckt die
 * Buttons trotzdem, wenn der User kein Admin ist.
 */
import { useEffect, useState } from "react";
import api, { formatApiError, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { FloppyDisk, Eye, ArrowCounterClockwise, Trash, FileDoc, ShieldCheck, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const ADMIN_ROLES = ["super_admin", "platform_admin", "admin"];

export default function LetterheadVersionsPanel() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapOpen, setSnapOpen] = useState(false);
  const [snapLabel, setSnapLabel] = useState("");
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/letterheads");
      setVersions(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const previewVersion = (id) => {
    // Open PDF in new tab — Bearer header can't be set on a target=_blank window,
    // so we fetch the blob and create an object URL instead.
    fetch(`${API}/letterheads/${id}/preview`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => { if (!r.ok) throw new Error("Vorschau konnte nicht geladen werden"); return r.blob(); })
      .then((blob) => { window.open(URL.createObjectURL(blob), "_blank"); })
      .catch((e) => toast.error(e.message));
  };

  const createSnapshot = async () => {
    if (!snapLabel.trim()) return;
    try {
      await api.post("/letterheads", { label: snapLabel.trim() });
      toast.success("Geschäftspapier-Version gespeichert");
      setSnapOpen(false); setSnapLabel("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (id) => {
    try {
      await api.post(`/letterheads/${id}/restore`);
      toast.success("Geschäftspapier aktiviert — wirkt sofort für neue Briefe");
      setConfirmRestore(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/letterheads/${id}`);
      toast.success("Version gelöscht");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 shadow-sm space-y-4" data-testid="letterhead-versions-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2">
            <FileDoc size={16} /> Geschäftspapier-Versionen
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl leading-relaxed">
            Speichere den aktuellen Stand deiner Briefkopf-Einstellungen (Logo, Adresse, Bank,
            Akzentfarbe, Signaturzeilen) als Version. Aktivierte Version wird sofort für
            <span className="font-semibold"> alle neuen Briefe</span> verwendet. Beim Versand eines
            einzelnen Briefes kannst du jederzeit eine andere Version auswählen.
          </p>
        </div>
        {isAdmin ? (
          <Button
            onClick={() => setSnapOpen(true)} className="bg-slate-900 hover:bg-slate-800 gap-2"
            data-testid="letterhead-create-version-btn"
          >
            <FloppyDisk size={16} /> Aktuellen Stand als Version speichern
          </Button>
        ) : (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
            <Lock size={12} /> Nur Administratoren können Versionen anlegen/aktivieren.
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Lade…</div>
      ) : versions.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-md py-10 text-center text-sm text-slate-500">
          Noch keine Versionen gespeichert. {isAdmin && "Lege oben einen ersten Snapshot an."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="letterhead-versions-list">
          {versions.map((v, idx) => (
            <div key={v.id} className="border border-slate-200 rounded-md p-3 flex items-center gap-3 hover:border-slate-300 transition-colors" data-testid={`letterhead-version-${v.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-sm text-slate-900 truncate">{v.label}</div>
                  {idx === 0 && (
                    <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-sm font-semibold inline-flex items-center gap-1">
                      <ShieldCheck size={10} /> Neueste
                    </span>
                  )}
                  {v.auto && (
                    <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">Auto-Backup</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {new Date(v.created_at).toLocaleString("de-DE")}
                  {v.created_by_name && <> · von {v.created_by_name}</>}
                  {v.snapshot?.company_name && <> · {v.snapshot.company_name}</>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => previewVersion(v.id)}
                        data-testid={`letterhead-preview-${v.id}`}>
                  <Eye size={14} /> Vorschau
                </Button>
                {isAdmin && (
                  <>
                    <Button size="sm" variant="outline" className="gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                            onClick={() => setConfirmRestore(v)} data-testid={`letterhead-restore-${v.id}`}>
                      <ArrowCounterClockwise size={14} /> Aktiv setzen
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-700 hover:bg-red-50 px-2"
                            onClick={() => setConfirmDelete(v)} data-testid={`letterhead-delete-${v.id}`}>
                      <Trash size={14} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Snapshot-Dialog */}
      <Dialog open={snapOpen} onOpenChange={setSnapOpen}>
        <DialogContent className="max-w-md" data-testid="letterhead-snapshot-dialog">
          <DialogHeader>
            <DialogTitle>Geschäftspapier-Version speichern</DialogTitle>
            <DialogDescription>
              Erstellt einen unveränderlichen Snapshot deines aktuellen Geschäftspapiers. Gib
              einen sprechenden Namen (z. B. „Briefkopf 2026 Q1") ein.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={snapLabel} onChange={(e) => setSnapLabel(e.target.value)}
            placeholder="z. B. Briefkopf 2026 Q1"
            data-testid="letterhead-snapshot-label"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") createSnapshot(); }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSnapOpen(false)}>Abbrechen</Button>
            <Button onClick={createSnapshot} disabled={!snapLabel.trim()}
                    className="bg-slate-900 hover:bg-slate-800" data-testid="letterhead-snapshot-save">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore-Confirm */}
      <Dialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Version „{confirmRestore?.label}" aktivieren?</DialogTitle>
            <DialogDescription>
              Diese Version wird sofort als aktives Geschäftspapier verwendet — alle neuen Briefe
              werden damit erstellt. Der bisherige aktive Stand wird automatisch als „Auto-Backup"-Version gesichert.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRestore(null)}>Abbrechen</Button>
            <Button onClick={() => restore(confirmRestore.id)} className="bg-emerald-700 hover:bg-emerald-800">
              Ja, aktivieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-Confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Version löschen?</DialogTitle>
            <DialogDescription>
              „{confirmDelete?.label}" wird unwiderruflich gelöscht. Briefe, die mit dieser Version
              versendet wurden, bleiben unverändert (das fertige PDF liegt in der Akte).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Abbrechen</Button>
            <Button onClick={() => remove(confirmDelete.id)} className="bg-red-700 hover:bg-red-800">
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
